import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SyncIcon from '@mui/icons-material/Sync';
import {
  orariTrasferimentoService,
  TrasferimentoRow,
} from '../services/orariTrasferimentoService';

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const DAY_NAMES: Record<string, string> = {
  mon: 'Lunedì',
  tue: 'Martedì',
  wed: 'Mercoledì',
  thu: 'Giovedì',
  fri: 'Venerdì',
  sat: 'Sabato',
  sun: 'Domenica',
};

const FASCIA_LABELS: Record<string, string> = {
  morning: 'Mattina',
  afternoon: 'Pomeriggio',
};

interface DaySlots {
  morning?: TrasferimentoRow;
  afternoon?: TrasferimentoRow;
}

interface OfficeData {
  displayName: string;
  area: string | null;
  location: string | null;
  days: Map<string, DaySlots>;
}

interface EditState {
  open: boolean;
  row: TrasferimentoRow | null;
  giorno: string;
  fascia: string;
  apertura: string;
  chiusura: string;
}

interface RenameState {
  open: boolean;
  ufficio_nome: string;
  display_name: string;
}

interface AddState {
  open: boolean;
  ufficio_nome: string;
  display_name: string;        // valorizzato quando si aggiunge a un ufficio esistente
  area: string;
  location: string;
  giorno: string;
  fascia: string;
  apertura: string;
  chiusura: string;
}

function formatSlot(slot?: TrasferimentoRow): string {
  if (!slot) return '—';
  return `${slot.apertura}–${slot.chiusura}`;
}

const OrariTrasferimentoPage: React.FC = () => {
  const [rows, setRows] = useState<TrasferimentoRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [editState, setEditState] = useState<EditState>({
    open: false, row: null, giorno: 'mon', fascia: 'morning', apertura: '', chiusura: '',
  });
  const [addState, setAddState] = useState<AddState>({
    open: false, ufficio_nome: '', display_name: '', area: '', location: '',
    giorno: 'mon', fascia: 'morning', apertura: '', chiusura: '',
  });

  const [renameState, setRenameState] = useState<RenameState>({
    open: false, ufficio_nome: '', display_name: '',
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await orariTrasferimentoService.getAll();
      setRows(data);
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Errore di caricamento');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const isStaticOnly = rows.length > 0 && rows.every((r) => r.is_static);

  const handleSync = async () => {
    try {
      const res = await orariTrasferimentoService.sync();
      setToast(`Sync OK: ${res.inserted} inseriti, ${res.updated} aggiornati, ${res.skipped} invariati`);
      fetchData();
    } catch (e: any) {
      setToast(`Errore sync: ${e?.message || e}`);
    }
  };

  const openEdit = (row: TrasferimentoRow) => {
    setEditState({
      open: true, row,
      giorno: row.giorno, fascia: row.fascia,
      apertura: row.apertura, chiusura: row.chiusura,
    });
  };

  const submitEdit = async () => {
    if (!editState.row) return;
    try {
      await orariTrasferimentoService.update(Number(editState.row.id), {
        giorno: editState.giorno,
        fascia: editState.fascia,
        apertura: editState.apertura,
        chiusura: editState.chiusura,
      });
      setEditState({ open: false, row: null, giorno: 'mon', fascia: 'morning', apertura: '', chiusura: '' });
      setToast('Riga aggiornata');
      fetchData();
    } catch (e: any) {
      setToast(`Errore: ${e?.message || e}`);
    }
  };

  const handleDelete = async (row: TrasferimentoRow) => {
    if (row.is_static) {
      setToast('Esegui prima Sync per persistere i dati statici nel DB');
      return;
    }
    if (!window.confirm(`Eliminare ${row.display_name || row.ufficio_nome} ${DAY_NAMES[row.giorno]} ${FASCIA_LABELS[row.fascia]}?`)) {
      return;
    }
    try {
      await orariTrasferimentoService.delete(Number(row.id));
      setToast('Riga eliminata');
      fetchData();
    } catch (e: any) {
      setToast(`Errore: ${e?.message || e}`);
    }
  };

  const submitRename = async () => {
    try {
      await orariTrasferimentoService.renameOffice(renameState.ufficio_nome, renameState.display_name);
      setRenameState({ ...renameState, open: false });
      setToast('Nome aggiornato');
      fetchData();
    } catch (e: any) {
      setToast(`Errore: ${e?.message || e}`);
    }
  };

  const submitAdd = async () => {
    try {
      await orariTrasferimentoService.create({
        ufficio_nome: addState.ufficio_nome,
        area: addState.area || null,
        location: addState.location || null,
        giorno: addState.giorno,
        fascia: addState.fascia,
        apertura: addState.apertura,
        chiusura: addState.chiusura,
      });
      setAddState({ ...addState, open: false });
      setToast('Riga creata');
      fetchData();
    } catch (e: any) {
      setToast(`Errore: ${e?.message || e}`);
    }
  };

  const openAddInline = (
    nome: string, displayName: string, area: string | null, location: string | null,
    giorno: string, fascia: string,
  ) => {
    setAddState({
      open: true,
      ufficio_nome: nome,
      display_name: displayName,
      area: area || '',
      location: location || '',
      giorno,
      fascia,
      apertura: '',
      chiusura: '',
    });
  };

  const grouped = new Map<string, OfficeData>();
  for (const r of rows) {
    if (!grouped.has(r.ufficio_nome)) {
      grouped.set(r.ufficio_nome, {
        displayName: r.display_name || r.ufficio_nome,
        area: r.area, location: r.location, days: new Map(),
      });
    }
    const data = grouped.get(r.ufficio_nome)!;
    if (!data.days.has(r.giorno)) data.days.set(r.giorno, {});
    const slots = data.days.get(r.giorno)!;
    if (r.fascia === 'morning') slots.morning = r; else slots.afternoon = r;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h4">Orari di trasferimento</Typography>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<SyncIcon />} variant="outlined" onClick={handleSync}>
            {isStaticOnly ? 'Sync (popola DB)' : 'Re-sync da static'}
          </Button>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={() => setAddState({ ...addState, open: true, ufficio_nome: '', display_name: '' })}
            disabled={isStaticOnly}
          >
            Aggiungi
          </Button>
        </Stack>
      </Stack>

      {loading && <CircularProgress />}
      {error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && grouped.size === 0 && (
        <Alert severity="info">
          Nessun orario di trasferimento configurato per il comune attivo.
        </Alert>
      )}

      {Array.from(grouped.entries()).map(([nome, data]) => (
        <Card key={nome} sx={{ mb: 2 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="h6">{data.displayName}</Typography>
              {!isStaticOnly && (
                <Tooltip title="Modifica nome">
                  <IconButton
                    size="small"
                    onClick={() => setRenameState({ open: true, ufficio_nome: nome, display_name: data.displayName })}
                  >
                    <EditIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
            {data.area && (
              <Typography variant="body2" color="text.secondary">
                Area: {data.area}
              </Typography>
            )}
            {data.location && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Sede: {data.location}
              </Typography>
            )}
            <Table size="small" sx={{ mt: 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Giorno</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Mattina</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Pomeriggio</TableCell>
                  <TableCell padding="checkbox" />
                </TableRow>
              </TableHead>
              <TableBody>
                {DAY_ORDER.filter((d) => data.days.has(d) || (!isStaticOnly && d !== 'sun')).map((d) => {
                  const slots = data.days.get(d) ?? {};
                  return (
                    <TableRow key={d} hover>
                      <TableCell>{DAY_NAMES[d]}</TableCell>
                      <TableCell>
                        {slots.morning ? (
                          <>
                            {formatSlot(slots.morning)}
                            {!slots.morning.is_static && (
                              <Tooltip title="Modifica mattina">
                                <IconButton size="small" onClick={() => openEdit(slots.morning!)}>
                                  <EditIcon fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </>
                        ) : !isStaticOnly ? (
                          <Tooltip title="Aggiungi mattina">
                            <IconButton size="small" onClick={() => openAddInline(nome, data.displayName, data.area, data.location, d, 'morning')}>
                              <AddIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        ) : '—'}
                      </TableCell>
                      <TableCell>
                        {slots.afternoon ? (
                          <>
                            {formatSlot(slots.afternoon)}
                            {!slots.afternoon.is_static && (
                              <Tooltip title="Modifica pomeriggio">
                                <IconButton size="small" onClick={() => openEdit(slots.afternoon!)}>
                                  <EditIcon fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </>
                        ) : !isStaticOnly ? (
                          <Tooltip title="Aggiungi pomeriggio">
                            <IconButton size="small" onClick={() => openAddInline(nome, data.displayName, data.area, data.location, d, 'afternoon')}>
                              <AddIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        ) : '—'}
                      </TableCell>
                      <TableCell padding="checkbox">
                        {slots.morning && !slots.morning.is_static && (
                          <Tooltip title="Elimina mattina">
                            <IconButton size="small" onClick={() => handleDelete(slots.morning!)}>
                              <DeleteIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {slots.afternoon && !slots.afternoon.is_static && (
                          <Tooltip title="Elimina pomeriggio">
                            <IconButton size="small" onClick={() => handleDelete(slots.afternoon!)}>
                              <DeleteIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ))}

      {/* Edit dialog */}
      <Dialog open={editState.open} onClose={() => setEditState({ ...editState, open: false })}>
        <DialogTitle>Modifica orario</DialogTitle>
        <DialogContent>
          {editState.row && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" gutterBottom>
                {editState.row.display_name || editState.row.ufficio_nome}
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                <Select
                  value={editState.giorno}
                  onChange={(e) => setEditState({ ...editState, giorno: String(e.target.value) })}
                  sx={{ minWidth: 140 }}
                >
                  {DAY_ORDER.map((d) => (
                    <MenuItem key={d} value={d}>{DAY_NAMES[d]}</MenuItem>
                  ))}
                </Select>
                <Select
                  value={editState.fascia}
                  onChange={(e) => setEditState({ ...editState, fascia: String(e.target.value) })}
                  sx={{ minWidth: 140 }}
                >
                  <MenuItem value="morning">Mattina</MenuItem>
                  <MenuItem value="afternoon">Pomeriggio</MenuItem>
                </Select>
              </Stack>
              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <TextField
                  label="Apertura (HH:MM)"
                  value={editState.apertura}
                  onChange={(e) => setEditState({ ...editState, apertura: e.target.value })}
                />
                <TextField
                  label="Chiusura (HH:MM)"
                  value={editState.chiusura}
                  onChange={(e) => setEditState({ ...editState, chiusura: e.target.value })}
                />
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditState({ ...editState, open: false })}>Annulla</Button>
          <Button variant="contained" onClick={submitEdit}>Salva</Button>
        </DialogActions>
      </Dialog>

      {/* Rename dialog */}
      <Dialog
        open={renameState.open}
        onClose={() => setRenameState({ ...renameState, open: false })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Modifica nome ufficio</DialogTitle>
        <DialogContent>
          <TextField
            label="Nome"
            value={renameState.display_name}
            onChange={(e) => setRenameState({ ...renameState, display_name: e.target.value })}
            helperText="È il nome che l'assistente dice al cittadino. Lascia vuoto per tornare al nome predefinito"
            fullWidth
            autoFocus
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameState({ ...renameState, open: false })}>Annulla</Button>
          <Button variant="contained" onClick={submitRename}>Salva</Button>
        </DialogActions>
      </Dialog>

      {/* Add dialog */}
      <Dialog open={addState.open} onClose={() => setAddState({ ...addState, open: false })}>
        <DialogTitle>Aggiungi orario</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {addState.display_name ? (
              <Typography variant="body2">{addState.display_name}</Typography>
            ) : (
              <TextField
                label="Ufficio"
                value={addState.ufficio_nome}
                onChange={(e) => setAddState({ ...addState, ufficio_nome: e.target.value })}
                required
              />
            )}
            <TextField
              label="Area (opzionale)"
              value={addState.area}
              onChange={(e) => setAddState({ ...addState, area: e.target.value })}
            />
            <TextField
              label="Sede (opzionale)"
              value={addState.location}
              onChange={(e) => setAddState({ ...addState, location: e.target.value })}
            />
            <Stack direction="row" spacing={2}>
              <Select
                value={addState.giorno}
                onChange={(e) => setAddState({ ...addState, giorno: String(e.target.value) })}
                sx={{ minWidth: 140 }}
              >
                {DAY_ORDER.map((d) => (
                  <MenuItem key={d} value={d}>{DAY_NAMES[d]}</MenuItem>
                ))}
              </Select>
              <Select
                value={addState.fascia}
                onChange={(e) => setAddState({ ...addState, fascia: String(e.target.value) })}
                sx={{ minWidth: 140 }}
              >
                <MenuItem value="morning">Mattina</MenuItem>
                <MenuItem value="afternoon">Pomeriggio</MenuItem>
              </Select>
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Apertura (HH:MM)"
                value={addState.apertura}
                onChange={(e) => setAddState({ ...addState, apertura: e.target.value })}
              />
              <TextField
                label="Chiusura (HH:MM)"
                value={addState.chiusura}
                onChange={(e) => setAddState({ ...addState, chiusura: e.target.value })}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddState({ ...addState, open: false })}>Annulla</Button>
          <Button variant="contained" onClick={submitAdd}>Crea</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast !== null}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        message={toast || ''}
      />
    </Box>
  );
};

export default OrariTrasferimentoPage;
